import { Heading } from "@/components/ui/Heading";
import { formatCurrency } from "@/utils/formatters";
import classes from "./DonationSummary.module.scss";
import type { DonationSummaryContentProps } from "./DonationSummaryProps";
import { useTranslation } from "@/hooks/useTranslation";
import { DisplayAmountWithConfig } from "@/components/DisplayAmount";

const DONATION_TYPES = {
  "one-off": "one-off donation",
  monthly: "monthly donation",
  // Recurring donations are billed monthly and, for migrated legacy forms, are
  // presented to the donor exactly like the former monthly donation.
  recurring: "monthly donation",
};

const EXTRA_AMOUNT = 1.25;

const calculateGiftAid = (amount: number) => {
  return amount * EXTRA_AMOUNT;
};

export function DonationSummaryContent(props: DonationSummaryContentProps) {
  const {
    donationType,
    donationAmount,
    isUKTaxPayer,
    isSubscribed,
    name,
    email,
    address,
    config,
    donationSource,
    cardSelector,
    cardEnabled,
    onBehalf,
    organisationName,
  } = props;

  const {
    cardType,
    sendCardToForename,
    sendCardToSurname,
    honouringForename,
    honouringSurname,
  } = cardSelector;

  const t = useTranslation();
  const donationTypeText = donationType
    ? DONATION_TYPES[donationType as keyof typeof DONATION_TYPES]
    : "";

  // i18n
  const donationItemText = t(
    `donation_summary.${config["donation-amount"].i18n}`,
    {
      donationtype: donationTypeText,
      donationamount: <DisplayAmountWithConfig />,
    },
  );
  const freeText = t(`donation_summary.${config.free.i18n}`);
  const ukTaxPayerText = t(`donation_summary.${config["gift-aid"].i18n}`, {
    amountGift: formatCurrency(calculateGiftAid(donationAmount ?? 0)),
  });
  const newsletterText = t(`donation_summary.${config.newsletter.i18n}`);
  const fundraisingText = t(`donation_summary.${config.fundraising.i18n}`);
  const honouringText = t(`donation_summary.${config.honouring.i18n}`, {
    forename: honouringForename,
    surname: honouringSurname,
  });

  const onBehalfText = t(
    `donation_summary.${config["on-behalf-of-organisation"].i18n}`,
    {
      organisationName,
    },
  );
  const cardText = t(`donation_summary.${config.card.i18n}`, {
    forename: sendCardToForename,
    surname: sendCardToSurname,
  });
  const tributeText = t(`donation_summary.${config.tribute.i18n}`);

  // Conditions
  const hasDonation = !!donationType && typeof donationAmount === "number";
  const showDonation = hasDonation && !!donationItemText;
  const showFreeText = hasDonation && !!freeText;
  const showNewsletter = isSubscribed && !!newsletterText;
  const showGiftAid = isUKTaxPayer && hasDonation && !!ukTaxPayerText;
  const hasDetails = !!name || !!email || !!address;
  const isFundraising = donationSource === "fundraising" && !!fundraisingText;
  const isHonouring = cardEnabled && !!honouringForename && !!honouringText;
  const showOnBehalf =
    donationSource === "on-behalf-of" &&
    !!onBehalf &&
    !!onBehalfText &&
    !!organisationName;
  const showCard =
    ["post", "e-card"].includes(cardType) && !!cardText && !!sendCardToForename;
  const showTribute = cardEnabled && !!tributeText;

  return (
    <>
      <Heading size="xl" gutterBottom>
        {t(`donation_summary.${config["donation-summary-title"].i18n}`)}
      </Heading>

      <ul className={classes.list}>
        {showDonation && (
          <li data-testid="list-item-donation-amount">{donationItemText}</li>
        )}
        {showFreeText && <li>{freeText}</li>}
        {showTribute && <li>{tributeText}</li>}
        {isHonouring && <li>{honouringText}</li>}
        {showCard && <li>{cardText}</li>}
        {isFundraising && <li>{fundraisingText}</li>}
        {showOnBehalf && <li>{onBehalfText}</li>}
        {showGiftAid && <li>{ukTaxPayerText}</li>}
        {showNewsletter && <li>{newsletterText}</li>}
      </ul>

      {hasDetails && (
        <>
          <div className={classes.divider} />
          <Heading as="h3" size="xl" gutterBottom>
            {t("_common.your-details")}
          </Heading>
          <ul className={classes.basicList}>
            {name && (
              <li>
                {t("_common.name")}: {name}
              </li>
            )}
            {email && (
              <li>
                {t("_common.email")}: {email}
              </li>
            )}
            {address && (
              <li>
                {t("_common.your-address")}: {address}
              </li>
            )}
          </ul>
        </>
      )}
    </>
  );
}
